/**
 * <gym-affiliate-mini-pdp> — Mini product dialog for the gym-affiliate page.
 *
 * Layout matches Figma `Website-V2` node 2006:11343:
 *   - Header (title + close)
 *   - 3-up image gallery thumbnails with dot pagination
 *   - One bordered card per product option ("Color : Black" + circular
 *     image swatches; active swatch shows a yellow ring)
 *   - Dashed divider
 *   - Price block (discounted + strikethrough MRP + green % OFF pill +
 *     "(Incl. of all taxes)") next to a yellow Add to Cart button
 *     (side-by-side on desktop, stacked on mobile)
 *
 * Behaviour:
 *   - Listens (delegated) for clicks on [data-gaff-atc-trigger]. Each
 *     trigger carries data-product-handle.
 *   - Fetches /products/{handle}.js (Shopify Ajax) and renders the dialog.
 *   - Applies a visual-only discount (default 15%) read from
 *     data-discount-percent on the host <dialog>. Cart receives items at
 *     full Shopify price.
 *   - ATC click POSTs /cart/add.js then dispatches the theme's `cart:update`
 *     event so <cart-drawer-component auto-open> opens automatically.
 *
 * Quantity defaults to 1 (no stepper, matches design).
 */

(function () {
  const DIALOG_SELECTOR = '[data-gaff-mini-pdp]';
  const TRIGGER_SELECTOR = '[data-gaff-atc-trigger]';
  const CART_EVENT = 'cart:update';
  const SOURCE_ID = 'gym-affiliate-mini-pdp';
  const GALLERY_PAGE_SIZE = 3;

  const productCache = new Map();

  function formatMoney(minor) {
    const value = (minor / 100).toFixed(2);
    const symbol =
      window.Shopify && window.Shopify.currency && window.Shopify.currency.active === 'INR'
        ? '₹'
        : window.Shopify && window.Shopify.currency
        ? window.Shopify.currency.active + ' '
        : '₹';
    if (symbol === '₹') {
      const [intPart, dec] = value.split('.');
      const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return symbol + formatted + (dec === '00' ? '' : '.' + dec);
    }
    return symbol + value;
  }

  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`fetch /products/${handle}.js failed: ${res.status}`);
    const data = await res.json();
    productCache.set(handle, data);
    return data;
  }

  function findVariant(product, selected) {
    return (
      product.variants.find((v) =>
        v.options.every((opt, idx) => opt === selected[idx]),
      ) || null
    );
  }

  function isOptionValueAvailable(product, position, value, otherSelected) {
    return product.variants.some((variant) => {
      if (!variant.available) return false;
      if (variant.options[position] !== value) return false;
      return variant.options.every((opt, idx) => {
        if (idx === position) return true;
        if (otherSelected[idx] == null) return true;
        return opt === otherSelected[idx];
      });
    });
  }

  /**
   * Find a representative image for a given option value at `position`.
   * For the first matching variant carrying this value, use its
   * `featured_image`. Returns null if no image is available — caller
   * should fall back to a text swatch.
   */
  function imageForOptionValue(product, position, value) {
    const variant = product.variants.find(
      (v) => v.options[position] === value && v.featured_image && v.featured_image.src,
    );
    if (variant && variant.featured_image) return variant.featured_image.src;
    return null;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) =>
      c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
    );
  }

  /**
   * Shopify's /products/{handle}.js returns `product.options` as an array
   * of objects in modern stores (`{name, position, values}`) but legacy
   * stores still return an array of strings. Normalise to a string name.
   */
  function optionName(opt) {
    if (!opt) return '';
    if (typeof opt === 'string') return opt;
    if (typeof opt === 'object' && opt.name) return opt.name;
    return String(opt);
  }

  /**
   * Image swatches are reserved for the "Color"/"Colour" option only.
   * Every other option (Size, Length, Gender, Quantity, etc.) renders
   * as text pill buttons.
   */
  function isColorOption(name) {
    const n = (name || '').toLowerCase().trim();
    return n === 'color' || n === 'colour';
  }

  class GymAffiliateMiniPdp extends HTMLElement {
    constructor() {
      super();
      this._dialog = null;
      this._product = null;
      this._selected = [];
      this._currentVariant = null;
      this._triggerEl = null;
      this._discountPercent = 15;
      this._galleryImages = [];
      this._galleryPage = 0;
    }

    connectedCallback() {
      this._dialog = this.closest('dialog') || document.querySelector(DIALOG_SELECTOR);
      if (!this._dialog) return;
      const pct = parseInt(this._dialog.dataset.discountPercent, 10);
      if (Number.isFinite(pct)) this._discountPercent = pct;

      this._dialog.addEventListener('click', this._handleBackdropClick);
      this.querySelector('[data-gaff-close]')?.addEventListener('click', () => this.close());
      this.querySelector('[data-gaff-add]')?.addEventListener('click', () => this._handleAdd());

      this._dialog.addEventListener('close', () => {
        if (this._triggerEl) {
          try { this._triggerEl.focus(); } catch (_) {}
        }
      });

      document.addEventListener('click', this._handleTriggerClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._handleTriggerClick);
      if (this._dialog) this._dialog.removeEventListener('click', this._handleBackdropClick);
    }

    _handleBackdropClick = (e) => {
      if (e.target === this._dialog) this.close();
    };

    _handleTriggerClick = (e) => {
      const trigger = e.target.closest(TRIGGER_SELECTOR);
      if (!trigger) return;
      e.preventDefault();
      const handle = trigger.dataset.productHandle;
      if (!handle) return;
      this._triggerEl = trigger;
      this.open(handle);
    };

    async open(handle) {
      if (!this._dialog) return;
      this._showLoading();
      try {
        if (typeof this._dialog.showModal === 'function') {
          this._dialog.showModal();
        } else {
          this._dialog.setAttribute('open', '');
        }
      } catch (_) {
        this._dialog.setAttribute('open', '');
      }
      try {
        const product = await fetchProduct(handle);
        this._product = product;
        this._initSelection();
        this._render();
      } catch (err) {
        this._setStatus('Could not load product. Try again.', 'error');
        this._hideLoading();
      }
    }

    close() {
      if (!this._dialog) return;
      try { this._dialog.close(); } catch (_) { this._dialog.removeAttribute('open'); }
      this._resetUI();
    }

    _resetUI() {
      this._product = null;
      this._selected = [];
      this._currentVariant = null;
      this._galleryImages = [];
      this._galleryPage = 0;
      this._setStatus('', null);
      this.querySelector('[data-gaff-options]').innerHTML = '';
      this.querySelector('[data-gaff-gallery]').innerHTML = '';
      const cta = this.querySelector('[data-gaff-add]');
      if (cta) {
        cta.removeAttribute('data-loading');
        cta.disabled = false;
      }
    }

    _initSelection() {
      const product = this._product;
      const firstAvailable = product.variants.find((v) => v.available) || product.variants[0];
      this._selected = firstAvailable ? firstAvailable.options.slice() : [];
      this._currentVariant = firstAvailable || null;
    }

    _showLoading() {
      this.querySelector('[data-gaff-loading]')?.removeAttribute('hidden');
      this.querySelector('[data-gaff-body]')?.setAttribute('hidden', '');
    }

    _hideLoading() {
      this.querySelector('[data-gaff-loading]')?.setAttribute('hidden', '');
      this.querySelector('[data-gaff-body]')?.removeAttribute('hidden');
    }

    _render() {
      const product = this._product;
      this._hideLoading();

      // Title
      this.querySelector('[data-gaff-title]').textContent = product.title;

      // Gallery
      this._galleryImages = Array.isArray(product.images) && product.images.length > 0
        ? product.images.slice(0, 9)
        : [product.featured_image].filter(Boolean);
      this._galleryPage = 0;
      this._renderGallery();

      // Options
      this._renderOptions();
      this._renderPrice();
      this._updateCtaState();
    }

    _renderGallery() {
      const wrap = this.querySelector('[data-gaff-gallery]');
      if (!wrap) return;
      wrap.innerHTML = '';
      this._galleryPage = 0;

      if (!this._galleryImages.length) return;

      // Single horizontal scroll track — all images live in one row so the
      // user can swipe / scroll horizontally. CSS scroll-snap snaps each
      // image to the start position.
      const track = document.createElement('div');
      track.className = 'gaff-mini-pdp__gallery-track';
      track.setAttribute('data-gaff-gallery-track', '');

      this._galleryImages.forEach((src, i) => {
        const cell = document.createElement('div');
        cell.className = 'gaff-mini-pdp__gallery-cell';
        if (src) {
          const img = document.createElement('img');
          img.src = src;
          img.alt = '';
          img.loading = i < 3 ? 'eager' : 'lazy';
          img.decoding = 'async';
          cell.appendChild(img);
        }
        track.appendChild(cell);
      });

      wrap.appendChild(track);

      // Pad to at least 3 cells so the viewport doesn't collapse when a
      // product has fewer than 3 images. Empty pads do not participate in
      // dot pagination.
      const realCount = this._galleryImages.length;
      for (let i = realCount; i < GALLERY_PAGE_SIZE; i++) {
        const cell = document.createElement('div');
        cell.className = 'gaff-mini-pdp__gallery-cell';
        track.appendChild(cell);
      }

      // Dots — one per "page" of GALLERY_PAGE_SIZE images. Only show if
      // there's more than one page.
      const pageCount = Math.ceil(realCount / GALLERY_PAGE_SIZE);
      if (pageCount > 1) {
        const dots = document.createElement('div');
        dots.className = 'gaff-mini-pdp__gallery-dots';
        dots.setAttribute('data-gaff-gallery-dots', '');
        for (let i = 0; i < pageCount; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'gaff-mini-pdp__gallery-dot';
          dot.setAttribute('aria-label', `Show images ${i * GALLERY_PAGE_SIZE + 1}-${Math.min((i + 1) * GALLERY_PAGE_SIZE, realCount)}`);
          dot.dataset.active = i === 0 ? 'true' : 'false';
          dot.dataset.page = String(i);
          dot.addEventListener('click', () => this._scrollGalleryToPage(i));
          dots.appendChild(dot);
        }
        wrap.appendChild(dots);
      }

      // Track scroll position to keep the active dot in sync. Throttle via
      // rAF so the update doesn't fire on every scroll tick.
      let rafId = null;
      track.addEventListener('scroll', () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          this._syncActiveDot(track);
        });
      });
    }

    _scrollGalleryToPage(pageIndex) {
      const track = this.querySelector('[data-gaff-gallery-track]');
      if (!track) return;
      const cells = track.querySelectorAll('.gaff-mini-pdp__gallery-cell');
      const targetIndex = Math.min(pageIndex * GALLERY_PAGE_SIZE, cells.length - 1);
      const targetCell = cells[targetIndex];
      if (!targetCell) return;
      track.scrollTo({ left: targetCell.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    }

    _syncActiveDot(track) {
      const dotsWrap = this.querySelector('[data-gaff-gallery-dots]');
      if (!dotsWrap) return;
      const cellWidth = track.querySelector('.gaff-mini-pdp__gallery-cell')?.offsetWidth || 1;
      // Divide scroll position by (cell + gap) width to get the current
      // leftmost cell index, then divide by page size to get the page.
      const cellWithGap = cellWidth + 4;
      const leftmostCellIndex = Math.round(track.scrollLeft / cellWithGap);
      const currentPage = Math.floor(leftmostCellIndex / GALLERY_PAGE_SIZE);
      this._galleryPage = currentPage;
      dotsWrap.querySelectorAll('.gaff-mini-pdp__gallery-dot').forEach((dot, i) => {
        dot.dataset.active = i === currentPage ? 'true' : 'false';
      });
    }

    _renderOptions() {
      const root = this.querySelector('[data-gaff-options]');
      if (!root) return;
      root.innerHTML = '';

      const product = this._product;
      const hasOnlyDefault =
        product.variants.length === 1 &&
        (product.options.length === 0 ||
          (product.options.length === 1 && product.variants[0].options[0] === 'Default Title'));
      if (hasOnlyDefault) return;

      product.options.forEach((opt, idx) => {
        const name = optionName(opt);
        const values = [...new Set(product.variants.map((v) => v.options[idx]))];
        const useImageSwatches = isColorOption(name);

        const card = document.createElement('div');
        card.className = 'gaff-mini-pdp__option';
        card.dataset.optionPosition = String(idx);
        card.innerHTML = `
          <div class="gaff-mini-pdp__option-label">
            ${escapeHtml(name)} : <span class="gaff-mini-pdp__option-value" data-gaff-option-value></span>
          </div>
          <div class="gaff-mini-pdp__option-values" role="group" aria-label="${escapeHtml(name)}"></div>
        `;
        const valuesWrap = card.querySelector('.gaff-mini-pdp__option-values');

        values.forEach((val) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.dataset.value = val;
          btn.dataset.position = String(idx);
          btn.setAttribute('aria-pressed', this._selected[idx] === val ? 'true' : 'false');
          btn.addEventListener('click', () => this._selectValue(idx, val));

          const imgSrc = useImageSwatches ? imageForOptionValue(product, idx, val) : null;
          if (imgSrc) {
            btn.className = 'gaff-mini-pdp__swatch';
            btn.setAttribute('aria-label', val);
            const img = document.createElement('img');
            img.className = 'gaff-mini-pdp__swatch-img';
            img.src = imgSrc;
            img.alt = '';
            img.loading = 'lazy';
            img.decoding = 'async';
            btn.appendChild(img);
          } else {
            btn.className = 'gaff-mini-pdp__swatch gaff-mini-pdp__swatch--text';
            btn.textContent = val;
          }
          valuesWrap.appendChild(btn);
        });

        root.appendChild(card);
      });

      this._updateAvailabilityAndLabels();
    }

    _selectValue(position, value) {
      this._selected[position] = value;
      this._currentVariant = findVariant(this._product, this._selected);
      this._updateAvailabilityAndLabels();
      this._renderPrice();
      this._updateCtaState();
    }

    _updateAvailabilityAndLabels() {
      this.querySelectorAll('.gaff-mini-pdp__option').forEach((row) => {
        const position = parseInt(row.dataset.optionPosition, 10);
        const valueLabel = row.querySelector('[data-gaff-option-value]');
        if (valueLabel) valueLabel.textContent = this._selected[position] || '';
        row.querySelectorAll('.gaff-mini-pdp__swatch').forEach((btn) => {
          const val = btn.dataset.value;
          const available = isOptionValueAvailable(this._product, position, val, this._selected);
          btn.setAttribute('aria-pressed', this._selected[position] === val ? 'true' : 'false');
          btn.setAttribute('aria-disabled', available ? 'false' : 'true');
        });
      });
    }

    _renderPrice() {
      const price = this._currentVariant ? this._currentVariant.price : this._product.price;
      const keep = (100 - this._discountPercent) / 100;
      // Floor to whole rupees so display reads "₹1,954" not "₹1,954.15"
      const discounted = Math.floor((price * keep) / 100) * 100;
      this.querySelector('[data-gaff-price]').textContent = formatMoney(discounted);
      const mrpWrap = this.querySelector('[data-gaff-mrp]');
      const compareEl = this.querySelector('[data-gaff-compare]');
      const pillEl = this.querySelector('[data-gaff-pill]');
      if (this._discountPercent > 0) {
        compareEl.textContent = formatMoney(price);
        mrpWrap.hidden = false;
        pillEl.textContent = this._discountPercent + '% OFF';
        pillEl.hidden = false;
      } else {
        mrpWrap.hidden = true;
        pillEl.hidden = true;
      }
    }

    _updateCtaState() {
      const cta = this.querySelector('[data-gaff-add]');
      if (!cta) return;
      const label = this.querySelector('[data-gaff-cta-label]');
      const setText = (t) => {
        if (label) label.textContent = t;
        else cta.textContent = t;
      };
      if (!this._currentVariant) {
        cta.disabled = true;
        setText('Unavailable');
        return;
      }
      if (!this._currentVariant.available) {
        cta.disabled = true;
        setText('Sold Out');
        return;
      }
      cta.disabled = false;
      setText('Add to Cart');
    }

    async _handleAdd() {
      if (!this._currentVariant) return;
      const cta = this.querySelector('[data-gaff-add]');
      cta.disabled = true;
      cta.setAttribute('data-loading', 'true');
      this._setStatus('', null);
      try {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: this._currentVariant.id, quantity: 1 }),
        });
        if (!addRes.ok) {
          const errBody = await addRes.json().catch(() => ({}));
          throw new Error(errBody.description || `cart/add.js failed: ${addRes.status}`);
        }
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await cartRes.json();

        const detail = {
          resource: cart,
          sourceId: SOURCE_ID,
          data: {
            didError: false,
            source: SOURCE_ID,
            productId: this._product.id,
            variantId: this._currentVariant.id,
            itemCount: cart.item_count,
          },
        };
        document.dispatchEvent(new CustomEvent(CART_EVENT, { bubbles: true, detail }));

        // Close shortly after so the cart drawer (auto-open listener)
        // takes over as the success indicator.
        setTimeout(() => this.close(), 250);
      } catch (err) {
        this._setStatus(err.message || 'Could not add to cart.', 'error');
        cta.removeAttribute('data-loading');
        cta.disabled = false;
      }
    }

    _setStatus(msg, state) {
      const el = this.querySelector('[data-gaff-status]');
      if (!el) return;
      el.textContent = msg || '';
      if (state) {
        el.dataset.state = state;
      } else {
        delete el.dataset.state;
      }
    }
  }

  if (!customElements.get('gym-affiliate-mini-pdp')) {
    customElements.define('gym-affiliate-mini-pdp', GymAffiliateMiniPdp);
  }
})();
